import { Message, Segment, Button, Icon, Grid, Checkbox, Dropdown } from "semantic-ui-react";
import _ from "lodash";
import {
  formatAssetMetadata,
  formatDate,
  formatFileSizeAndDuration,
  getProcessedAssetsForAsset,
  getFileSizeLabel,
  handleDownloadClick,
  isShownInProcessedAssets,
  showElementFullTextPopup,
} from "services/common";
import { useDispatch } from "react-redux";
import { useEffect, useMemo, useState, useRef } from "react";
import ConfirmationModal from "../ConfirmationModal/ConfirmationModal";
import LoadResolver from "../LoadResolver";
import SchedulingNotesModal from "components/SchedulingNotesModal/SchedulingNotesModal";
import AssetImage from "components/AssetImage/AssetImage";

const ResolutionsListElelments = ({ processedResolutions, handleChange, checkedState }) => {
  const processedNameRef = useRef();
  return processedResolutions.map((resolution, r) => (
    <li key={r}>
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          padding: ".5rem 0",
          gap: "2rem",
        }}
      >
        <strong style={{ flexGrow: "1", maxWidth: "7rem" }}>{resolution.resolution}</strong>
        <a
          style={{ flexGrow: "3", width: "20rem", overflow: "hidden" }}
          key={resolution.id}
          href={resolution.mediaUrl}
          download
          ref={processedNameRef}
        >
          {showElementFullTextPopup(processedNameRef, resolution.name, "bold 1rem Lato", "#f8f8f9")}
        </a>
        <Checkbox
          style={{ flexGrow: "1", maxWidth: "2rem" }}
          key={r}
          checked={checkedState[r]}
          onChange={() => handleChange(r)}
        ></Checkbox>
      </div>
    </li>
  ));
};

const ProcessedAssetsRow = ({
  asset,
  processedAssets,
  parentAssetId,
  onAssetSelected,
  handleParentSelectorChange,
  parentSelector,
}) => {
  const processedResolutions = useMemo(() => {
    return getProcessedAssetsForAsset(asset, processedAssets).filter(
      (processedAsset) =>
        processedAsset.id === asset.id || processedAsset.media.split("/")[2] === parentAssetId
    );
  }, [asset, parentAssetId, processedAssets]);

  const [checkedState, setCheckedState] = useState(
    new Array(processedResolutions.length).fill(false)
  );

  const handleChange = (position) => {
    const updatedCheckedState = checkedState.map((item, index) => {
      return index === position ? !item : item;
    });
    const asset = processedResolutions[position];
    const isChecked = updatedCheckedState[position];
    setCheckedState(updatedCheckedState);
    onAssetSelected({ asset, isChecked });
  };

  useEffect(() => {
    const selectedElements = checkedState.filter((isChecked) => isChecked === true);
    selectedElements.length > 0 && selectedElements.length < checkedState.length
      ? handleParentSelectorChange("defaultIndeterminate")
      : selectedElements.length > 0 && selectedElements.length === checkedState.length
      ? handleParentSelectorChange("checked")
      : handleParentSelectorChange("unchecked");
  }, [checkedState]);

  useEffect(() => {
    if (parentSelector === "checked") {
      setCheckedState(new Array(processedResolutions.length).fill(true));
    }
    if (parentSelector === "unchecked") {
      setCheckedState(new Array(processedResolutions.length).fill(false));
    }
  }, [parentSelector]);
  return (
    <Grid.Column width={12}>
      <ol style={{ padding: "0 0 0 1rem", margin: "0", display: "flex", flexDirection: "column" }}>
        <ResolutionsListElelments
          processedResolutions={processedResolutions}
          handleChange={handleChange}
          checkedState={checkedState}
        />
      </ol>
    </Grid.Column>
  );
};

const ProcessedAssetsInfo = ({
  parentAssetId,
  processedAssets,
  asset,
  t,
  onAssetSelected,
  selectedAssets,
  setSelectedAssets,
  campaign,
}) => {
  const ASSET_KEYS = [
    "name",
    "imgSrc",
    "resolution",
    "type",
    "fileSize",
    "framerate",
    "metadata",
    "notes",
    "digitalServiceSender",
    "digitalServiceNames",
    "digitalServiceDate",
  ];
  const [parentSelector, setParentSelector] = useState("unchecked");

  const handleChange = (id) => {
    parentSelector === "checked" || parentSelector === "defaultIndeterminate"
      ? setParentSelector("unchecked")
      : setParentSelector("checked");

    const processedResolutionsList = (id) => {
      return getProcessedAssetsForAsset(asset, processedAssets).filter(
        (processedAsset) =>
          processedAsset.id === asset.id || processedAsset.media.split("/")[2] === id
      );
    };

    const newSelectedAssets =
      parentSelector === "unchecked"
        ? _.concat(selectedAssets, processedResolutionsList(asset.id))
        : _.difference(selectedAssets, processedResolutionsList(asset.id));
    setSelectedAssets(newSelectedAssets);
  };

  const handleParentSelectorChange = (value) => {
    setParentSelector(value);
  };
  const attributeRef = useRef(null);

  return (
    <>
      <Grid columns={3}>
        <Grid.Column width={3}>
          <a
            href={asset.mediaUrl}
            target="_blank"
            rel="noreferrer"
            style={{
              display: "flex",
              alignItems: "center",
              flexDirection: "column",
              gap: "1rem",
            }}
            onClick={(e) => handleDownloadClick(e, asset.mediaUrl, t)}
          >
            <AssetImage asset={asset} />
            <span>
              <Icon name="cloud download" /> {t("asset_download")}
            </span>
          </a>
        </Grid.Column>
        <Grid.Column width={12}>
          <Grid.Row style={{ paddingBottom: "1.5rem" }}>
            <Grid columns={3} className="info-grid">
              <Grid.Column
                width={1}
                floated="right"
                style={{ display: "flex", justifyContent: "left" }}
              >
                <Checkbox
                  indeterminate={parentSelector === "defaultIndeterminate"}
                  checked={parentSelector === "checked"}
                  style={{ marginTop: "1rem" }}
                  onClick={() => handleChange(asset.id)}
                />
              </Grid.Column>
              {ASSET_KEYS.map((key) =>
                asset[key] || (key === "fileSize" && (asset["fileSize"] || asset["duration"])) ? (
                  <Grid.Row key={key} style={{ paddingBottom: ".5rem" }}>
                    <Grid.Column width={6}>
                      {key === "fileSize"
                        ? getFileSizeLabel(asset["fileSize"], asset["duration"], t)
                        : t("asset_" + key)}
                    </Grid.Column>
                    <Grid.Column width={10}>
                      <div style={{ width: "95%", overflow: "hidden" }} ref={attributeRef}>
                        {key === "type"
                          ? `${asset[key].split("/")[1].toUpperCase()}`
                          : key === "metadata"
                          ? formatAssetMetadata(asset[key], campaign.duration || [])
                          : key === "fileSize"
                          ? formatFileSizeAndDuration(asset[key], asset["duration"])
                          : key === "digitalServiceDate"
                          ? formatDate(asset[key])
                          : key === "framerate"
                          ? `${asset[key]} FPS`
                          : showElementFullTextPopup(
                              attributeRef,
                              asset[key],
                              "normal 1rem Lato",
                              "#f8f8f9"
                            )}
                      </div>
                    </Grid.Column>
                  </Grid.Row>
                ) : null
              )}
            </Grid>
          </Grid.Row>
          <Grid.Row>
            <Grid columns={3} className="info-grid">
              <Grid.Column width={6}>
                <h4>{t("asset_processed_assets")}</h4>
              </Grid.Column>
              <Grid.Column width={10}></Grid.Column>
              <ProcessedAssetsRow
                asset={asset}
                processedAssets={processedAssets}
                parentAssetId={parentAssetId}
                onAssetSelected={onAssetSelected}
                handleParentSelectorChange={handleParentSelectorChange}
                parentSelector={parentSelector}
              />
            </Grid>
          </Grid.Row>
        </Grid.Column>
      </Grid>
    </>
  );
};

const DownloadButtons = ({
  processedAssetsDownload,
  isDownloadingAssets,
  downloadAssets,
  selectedAssets,
  t,
}) => {
  return (
    <>
      <Button
        style={{ fontSize: "1rem", marginLeft: "0px" }}
        type="button"
        className="naked-button"
        onClick={() => downloadAssets(processedAssetsDownload())}
        disabled={isDownloadingAssets}
      >
        <Icon
          name={isDownloadingAssets ? "spinner" : "cloud download"}
          loading={isDownloadingAssets}
        />
        {t("campaign_display_download_all_assets")}
      </Button>
      <Button
        style={{ fontSize: "1rem", marginLeft: "0px" }}
        type="button"
        className="naked-button"
        onClick={() => downloadAssets(selectedAssets)}
        disabled={selectedAssets.length === 0 || isDownloadingAssets}
      >
        <Icon
          name={isDownloadingAssets ? "spinner" : "cloud download"}
          loading={isDownloadingAssets}
        />
        {t("campaign_display_download_selected_assets")}
      </Button>
    </>
  );
};

const RemoveAssetsButtons = ({
  selectedAssets,
  setIsConfirmationModalOpen,
  setAssetsToDelete,
  isDeleting,
  t,
}) => {
  return (
    <>
      <Button
        style={{ fontSize: "1rem", marginLeft: "0px" }}
        type="button"
        className="naked-button"
        onClick={() => {
          setIsConfirmationModalOpen(true);
          setAssetsToDelete("all");
        }}
        disabled={isDeleting}
      >
        <Icon name={isDeleting ? "spinner" : "trash"} loading={isDeleting} />
        {t("campaign_display_delete_all_assets")}
      </Button>
      <Button
        style={{ fontSize: "1rem", marginLeft: "0px" }}
        type="button"
        className="naked-button"
        onClick={() => {
          setIsConfirmationModalOpen(true);
          setAssetsToDelete("selected");
        }}
        disabled={selectedAssets.length === 0 || isDeleting}
      >
        <Icon name={isDeleting ? "spinner" : "trash"} loading={isDeleting} />
        {t("campaign_display_delete_selected_assets")}
      </Button>
    </>
  );
};

const SendSelectedAssets = ({
  selectedAssets,
  digitalServiceOptions,
  isSubmitting,
  setModalOpen,
  selectedAgencies,
  setSelectedAgencies,
  t,
}) => {
  const handleChange = (e, { value }) => {
    const selectedAgenciesList = digitalServiceOptions.filter((agency) =>
      value.includes(agency.value)
    );
    setSelectedAgencies(selectedAgenciesList);
  };
  return (
    <div style={{ display: "flex", gap: "1rem", alignItems: "center" }}>
      <span>{t("campaign_display_send_selected_assets")}</span>
      <Dropdown
        placeholder={t("campaign_display_select_digital_service")}
        search
        selection
        clearable
        multiple
        selectOnNavigation={false}
        options={digitalServiceOptions}
        onChange={handleChange}
      />
      <Button
        primary
        style={{ margin: "0" }}
        loading={isSubmitting}
        disabled={
          selectedAssets.length === 0 ||
          !selectedAssets ||
          !selectedAgencies ||
          selectedAgencies.length === 0
        }
        onClick={() => setModalOpen(true)}
      >
        {t("campaign_display_send_button")}
      </Button>
    </div>
  );
};

const CampaignDisplayProcessedAssets = ({
  assets,
  campaign,
  currentAssets,
  isPolling,
  processedAssets,
  isDownloadingAssets,
  downloadAssets,
  setProcessedAssetsViewOpen,
  digitalServiceOptions,
  isSubmitting,
  submitDigitalAssets,
  t,
}) => {
  const sortedOptions = digitalServiceOptions.sort((a, b) => a.text.localeCompare(b.text));
  const dispatch = useDispatch();
  const processedAssetsList = useMemo(() => {
    const filteredProcessedAssets = assets?.filter((asset) =>
      isShownInProcessedAssets(asset, processedAssets)
    );
    return filteredProcessedAssets;
  }, [assets, processedAssets]);
  const [selectedAssets, setSelectedAssets] = useState([]);
  const [selectedAgencies, setSelectedAgencies] = useState([]);
  const [searchInputValue] = useState("");
  const [assetsToRender, setAssetsToRender] = useState(processedAssetsList);
  const [assetsToDelete, setAssetsToDelete] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [confirmationModalData, setConfirmationModalData] = useState({
    icon: "trash",
    headerText: t("remove_processed_assets_modal_header"),
    text: t("remove_processed_assets_modal_text", { selectionNumber: assetsToDelete }),
  });
  const [submitModalOpen, setSubmitModalOpen] = useState(false);

  useEffect(() => {
    if (assets?.length === 0) {
      setProcessedAssetsViewOpen(false);
    }
  }, [assets, setProcessedAssetsViewOpen]);
  // If the assets are still under processing, we start polling process until all
  // assets have been processed
  useEffect(() => {
    if (assets.filter((item) => item.status === "PROCESSING_PENDING").length > 0)
      dispatch.assets.loadCampaignProcessedAssets(campaign);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  useEffect(
    () =>
      assetsToDelete !== "" &&
      setConfirmationModalData({
        icon: "trash",
        headerText: t("remove_processed_assets_modal_header"),
        text: t("remove_processed_assets_modal_text", {
          selectionText: assetsToDelete === "all" ? t("all") : t("selected"),
          selectionNumber:
            assetsToDelete === "all" ? processedAssets.length : selectedAssets.length,
        }),
      }),
    [assetsToDelete]
  );
  const onAssetSelected = ({ asset, isChecked }) => {
    const newSelectedAssets = isChecked
      ? _.concat(selectedAssets, asset)
      : _.without(selectedAssets, asset);
    setSelectedAssets(newSelectedAssets);
  };

  useEffect(() => {
    const searchResults = assets.filter((asset) =>
      asset.name.toString().toLowerCase().includes(searchInputValue.toString().toLowerCase())
    );
    searchInputValue !== " "
      ? setAssetsToRender(searchResults)
      : setAssetsToRender(processedAssetsList);
  }, [searchInputValue]);
  const processedAssetsDownload = () => {
    const processedAssetsByOriginalAsset = processedAssetsList.map((asset) =>
      getProcessedAssetsForAsset(asset, processedAssets)
    );
    return processedAssetsByOriginalAsset.reduce((acc, curVal) => acc.concat(curVal), []);
  };

  const handleDeleteProcessedAssets = async () => {
    if (assetsToDelete === "all") {
      await dispatch.assets.deleteProcessedAssets(processedAssets, campaign);
    } else {
      await dispatch.assets.deleteProcessedAssets(selectedAssets, campaign);
    }
    setAssetsToDelete("");
  };
  return (
    <LoadResolver isLoading={isPolling} hideWhenLoading>
      <div style={{ display: "flex", flexDirection: "row", justifyContent: "flex-end" }}>
        <DownloadButtons
          processedAssetsDownload={processedAssetsDownload}
          selectedAssets={selectedAssets}
          isDownloadingAssets={isDownloadingAssets}
          downloadAssets={downloadAssets}
          t={t}
        />
        <RemoveAssetsButtons
          selectedAssets={selectedAssets}
          processedAssets={processedAssets}
          setIsConfirmationModalOpen={setIsConfirmationModalOpen}
          setAssetsToDelete={setAssetsToDelete}
          isDeleting={isDeleting}
          t={t}
        />
        <SendSelectedAssets
          selectedAssets={selectedAssets}
          digitalServiceOptions={sortedOptions}
          isSubmitting={isSubmitting}
          setModalOpen={setSubmitModalOpen}
          selectedAgencies={selectedAgencies}
          setSelectedAgencies={setSelectedAgencies}
          t={t}
        />
      </div>

      {assets?.length > 0 ? (
        <>
          {assetsToRender.length > 0 ? (
            assetsToRender.map((asset, index) => {
              return (
                <Segment key={index} className="asset-input-item">
                  <ProcessedAssetsInfo
                    asset={asset}
                    t={t}
                    onAssetSelected={onAssetSelected}
                    parentAssetId={asset.id}
                    processedAssets={processedAssets}
                    setSelectedAssets={setSelectedAssets}
                    selectedAssets={selectedAssets}
                    campaign={campaign}
                  />
                </Segment>
              );
            })
          ) : (
            <Grid container={true} textAlign="center">
              <Grid.Row columns={1}>
                <Grid.Column width={12} style={{ height: "3rem" }}>
                  <h4>{t("campaigns_no_search_results")}</h4>
                </Grid.Column>
              </Grid.Row>
            </Grid>
          )}
        </>
      ) : (
        <Message>
          {currentAssets.length === 0
            ? t("campaign_display_no_defined_assets")
            : t("campaign_display_no_access_assets")}
        </Message>
      )}
      <ConfirmationModal
        data={confirmationModalData}
        open={isConfirmationModalOpen}
        setOpen={setIsConfirmationModalOpen}
        onSuccess={async () => {
          setIsDeleting(true);
          try {
            await handleDeleteProcessedAssets();
          } catch (err) {
            console.error(err);
          } finally {
            setIsDeleting(false);
          }
        }}
        dataForOnSuccess={{}}
      />

      <SchedulingNotesModal
        agencies={selectedAgencies}
        open={submitModalOpen}
        campaign={campaign}
        setModalOpen={setSubmitModalOpen}
        selectedAssets={selectedAssets}
        submitDigitalAssets={submitDigitalAssets}
      />
    </LoadResolver>
  );
};

export default CampaignDisplayProcessedAssets;
